import React, { useContext, useRef, useState } from "react";
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress, Paper, TextField, Tooltip, Typography } from "@material-ui/core";
import { Add, ChevronLeft, ChevronRight, Close, DeleteOutline, EditOutlined, ErrorOutline, LabelOutlined, PeopleOutline, Refresh, Search } from "@material-ui/icons";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import TagModal from "../../components/TagModal";
import ContactTagListModal from "../../components/ContactTagListModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import CrmTheme from "../CrmRelationship/theme";
import useTags from "./useTags";
import useStyles from "./styles";

function TagsContent() {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const list = useTags({ companyId: user.companyId, socket });
  const [editor, setEditor] = useState(null);
  const [contactTag, setContactTag] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const deletingRef = useRef(false);
  const first = list.tags.length ? (list.pageNumber - 1) * 20 + 1 : 0;
  const closeDelete = () => { if (!deletingRef.current) setDeleting(null); };

  const deleteTag = async () => {
    if (!deleting || deletingRef.current) return;
    deletingRef.current = true;
    setBusy(true);
    try {
      await api.delete(`/tags/${deleting.id}`);
      toast.success("Tag excluída.");
      setDeleting(null);
      list.refresh();
    } catch (err) { toastError(err); }
    finally { deletingRef.current = false; setBusy(false); }
  };

  return (
    <section className={classes.root} aria-label="Gerenciar tags">
      <header className={classes.header}>
        <div className={classes.heading}>
          <span className={classes.headingIcon}><LabelOutlined /></span>
          <Typography component="h1" className={classes.title}>Tags</Typography>
          <Chip size="small" label={list.count} className={classes.count} />
        </div>
        <Button color="primary" variant="contained" startIcon={<Add />} className={classes.add} onClick={() => setEditor({})}>Nova tag</Button>
      </header>
      <Paper variant="outlined" className={classes.panel}>
        <div className={classes.toolbar}>
          <TextField type="search" placeholder="Buscar tag por nome ou cor" variant="outlined" size="small" className={classes.search}
            value={list.search} onChange={event => list.setSearch(event.target.value)} inputProps={{ "aria-label": "Buscar tag" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
          <Tooltip title="Atualizar tags"><span><IconButton aria-label="Atualizar tags" disabled={list.loading} onClick={list.refresh} className={classes.action}><Refresh /></IconButton></span></Tooltip>
        </div>
        <div className={classes.progress}>{list.loading && <LinearProgress />}</div>
        {list.error && <div role="alert" className={classes.error}><ErrorOutline /><span>Não foi possível carregar as tags.</span><Button onClick={list.refresh}>Tentar novamente</Button></div>}
        <div className={classes.columns} aria-hidden="true"><span>Tag</span><span>Contatos</span><span>Ações</span></div>
        <div className={classes.rows} aria-label="Lista de tags" role="region" aria-busy={list.loading}>
          {list.tags.map(tag => (
            <article key={tag.id} aria-label={tag.name} className={classes.row}>
              <div className={classes.tagIdentity}>
                <span className={classes.tagIcon}><LabelOutlined style={{ color: tag.color }} /></span>
                <Tooltip title={tag.name}><span className={classes.name}>{tag.name}</span></Tooltip>
              </div>
              <Button className={classes.contacts} startIcon={<PeopleOutline />} onClick={() => setContactTag(tag)} aria-label={`Ver contatos de ${tag.name}`}>
                {tag.contacts?.length || 0}<span className={classes.contactLabel}> {(tag.contacts?.length || 0) === 1 ? "contato" : "contatos"}</span>
              </Button>
              <div className={classes.actions}>
                <Tooltip title="Editar tag"><IconButton className={classes.action} aria-label={`Editar ${tag.name}`} onClick={() => setEditor(tag)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Excluir tag"><IconButton className={`${classes.action} ${classes.delete}`} aria-label={`Excluir ${tag.name}`} onClick={() => setDeleting(tag)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
              </div>
            </article>
          ))}
          {!list.tags.length && <div className={classes.empty}>
            {list.loading ? <CircularProgress size={28} aria-label="Carregando tags" /> : !list.error && <>
              <LabelOutlined /><Typography>{list.searchParam ? "Nenhuma tag encontrada" : "Nenhuma tag cadastrada"}</Typography>
              <Button color="primary" onClick={() => list.searchParam ? list.setSearch("") : setEditor({})}>{list.searchParam ? "Limpar busca" : "Criar primeira tag"}</Button>
            </>}
          </div>}
        </div>
        <footer className={classes.footer}>
          <span aria-live="polite">{first}–{first ? first + list.tags.length - 1 : 0} de {list.count}</span>
          <div className={classes.pagination}>
            <IconButton className={classes.action} aria-label="Página anterior" disabled={list.loading || list.pageNumber === 1} onClick={() => list.setPage(list.pageNumber - 1)}><ChevronLeft /></IconButton>
            <span>Página {list.pageNumber}</span>
            <IconButton className={classes.action} aria-label="Próxima página" disabled={list.loading || !list.hasMore} onClick={() => list.setPage(list.pageNumber + 1)}><ChevronRight /></IconButton>
          </div>
        </footer>
      </Paper>
      {editor && <TagModal open tagId={editor.id} kanban={0} onClose={() => setEditor(null)} onSaved={list.refresh} />}
      {contactTag && <ContactTagListModal open tag={contactTag} onClose={() => setContactTag(null)} onChanged={list.refresh} />}
      <Dialog open={Boolean(deleting)} onClose={closeDelete} maxWidth="xs" fullWidth aria-labelledby="delete-tag-title">
        <DialogTitle id="delete-tag-title">Excluir tag?<IconButton className={classes.dialogClose} disabled={busy} onClick={closeDelete} aria-label="Fechar confirmação"><Close /></IconButton></DialogTitle>
        <DialogContent><Typography className={classes.deleteName}>{deleting?.name}</Typography><Typography color="textSecondary">A tag será removida dos contatos e atendimentos vinculados. Os contatos serão mantidos.</Typography></DialogContent>
        <DialogActions><Button variant="outlined" disabled={busy} onClick={closeDelete}>Cancelar</Button><Button variant="contained" color="primary" disabled={busy} onClick={deleteTag}>{busy ? "Excluindo…" : "Excluir tag"}</Button></DialogActions>
      </Dialog>
    </section>
  );
}

export default function Tags() { return <CrmTheme><TagsContent /></CrmTheme>; }
